import React, { useState } from 'react';
import { Upload, X, Copy, Check, CreditCard, Banknote, Loader } from 'lucide-react';
import { config } from '../../config';

const PaymentModal = ({ isOpen, onClose, invoice, onSuccess }) => {
    if (!isOpen || !invoice) {return null;}

    const [paymentMethod, setPaymentMethod] = useState('EFT'); // 'EFT' | 'PAYFAST'
    const [uploading, setUploading] = useState(false);
    const [copied, setCopied] = useState(null);
    const [dragActive, setDragActive] = useState(false);

    const reference = invoice.reference || invoice.id || `INV-${new Date().getTime().toString().slice(-6)}`;

    const bankDetails = {
        Bank: 'FNB',
        Account: '62812345678',
        Branch: '250655',
        Ref: reference
    };

    const copyToClipboard = (key, text) => {
        navigator.clipboard.writeText(text);
        setCopied(key);
        setTimeout(() => setCopied(null), 2000);
    };

    const handleUpload = async (file) => {
        if (!file) {return;}

        setUploading(true);
        try {
            const reader = new FileReader();
            reader.onloadend = async () => {
                const base64String = reader.result.replace('data:', '').replace(/^.+,/, '');

                await fetch(`${config.API_BASE_URL}/webhook/billing/upload-pop`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        filename: file.name,
                        data: base64String,
                        invoice_id: invoice.id,
                        reference: reference,
                        amount: invoice.total || invoice.amount
                    })
                });
                setUploading(false);
                if (onSuccess) {onSuccess();}
                onClose();
                alert('Success! Proof of Payment uploaded. Access will be unlocked shortly.');
            };
            reader.readAsDataURL(file);
        } catch (error) {
            console.error(error);
            setUploading(false);
            alert('Upload failed.');
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {setDragActive(true);}
        else if (e.type === "dragleave") {setDragActive(false);}
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleUpload(e.dataTransfer.files[0]);
        }
    };

    const handlePayNow = async () => {
        try {
            setUploading(true);

            // Fetch signed Payfast URL from server (never expose credentials client-side)
            const response = await fetch(`${config.API_BASE_URL}/webhook/payfast/generate-url`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    invoice_id: invoice.id,
                    amount: (invoice.total || invoice.amount).toFixed(2),
                    description: invoice.description || `Invoice #${invoice.id}`,
                    email: invoice.email || config.USER_EMAIL
                })
            });

            const { paymentUrl } = await response.json();

            if (!paymentUrl) {throw new Error('Failed to generate payment URL');}

            // Redirect to Payfast
            window.location.href = paymentUrl;
        } catch (error) {
            console.error('Payment initiation failed:', error);
            alert('Unable to process payment. Please try manual EFT or contact support.');
            setUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden animate-in zoom-in-95 duration-200 relative">
                <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors">
                    <X size={20} />
                </button>

                <div className="p-8">
                    <div className="text-center mb-8">
                        <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-2">Secure Payment</p>
                        <h2 className="text-3xl font-black text-slate-800 tracking-tight">
                            R {(invoice.total || invoice.amount).toLocaleString('en-ZA')}
                        </h2>
                        <p className="text-slate-500 mt-2 font-medium">{invoice.description || `Invoice #${invoice.id}`}</p>
                    </div>

                    <div className="flex bg-slate-100 p-1 rounded-xl mb-8">
                        <button
                            onClick={() => setPaymentMethod('EFT')}
                            className={`flex-1 py-2 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 ${paymentMethod === 'EFT' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            <Banknote size={16} /> Manual EFT
                        </button>
                        <button
                            onClick={() => setPaymentMethod('PAYFAST')}
                            className={`flex-1 py-2 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 ${paymentMethod === 'PAYFAST' ? 'bg-white text-red-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            <CreditCard size={16} /> PayFast
                        </button>
                    </div>

                    {paymentMethod === 'EFT' && (
                        <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
                            <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-5 space-y-3">
                                {Object.entries(bankDetails).map(([key, val]) => (
                                    <div key={key} className="flex justify-between items-center group cursor-pointer" onClick={() => copyToClipboard(key, val)}>
                                        <span className="text-xs font-bold text-blue-300 uppercase tracking-wider">{key}</span>
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono font-bold text-slate-700 group-hover:text-blue-600 transition-colors">{val}</span>
                                            {copied === key ? <Check size={14} className="text-green-500" /> : <Copy size={14} className="text-blue-200 opacity-0 group-hover:opacity-100 transition-opacity" />}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div
                                className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                            >
                                <input
                                    type="file"
                                    id="pop-upload"
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    onChange={(e) => handleUpload(e.target.files[0])}
                                    accept="image/*,application/pdf"
                                    disabled={uploading}
                                />
                                <div className="pointer-events-none">
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 transition-colors ${uploading ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`}>
                                        <Upload size={24} className={uploading ? 'animate-bounce' : ''} />
                                    </div>
                                    <p className="font-bold text-slate-700">{uploading ? 'Uploading...' : 'Drop Proof of Payment Here'}</p>
                                    <p className="text-xs text-slate-400 mt-1">or click to browse</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {paymentMethod === 'PAYFAST' && (
                        <div className="text-center py-8 animate-in slide-in-from-right-4 duration-300">
                            <p className="text-slate-500 mb-4 text-sm font-medium">Redirecting to secure gateway...</p>
                            <button
                                onClick={handlePayNow}
                                className="bg-[#bf0b0b] text-white w-full py-4 rounded-xl font-bold uppercase tracking-wide hover:bg-[#a60909] transition-colors shadow-lg shadow-red-100"
                            >
                                Pay Now with PayFast
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PaymentModal;
